import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import LoginIcon from "@mui/icons-material/Login";
import { invitationLoginOptions } from "../../services/invitationParams";
import LogoutIcon from "@mui/icons-material/Logout";
import logo from "../../images/cooktop_logo.png";
import { Link, useLocation } from "react-router-dom";
import Button from "../ui/Button";
import { NAV_ITEMS } from "./navItems";
import { useViewer, can } from "../../services/viewer";

export const NAVBAR_HEIGHT = 72;

function Navbar() {
  const { isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const { pathname } = useLocation();

  // The viewer is only asked for once signed in: unauthenticated, the request
  // would 401 and bounce straight back to Auth0.
  const { data: viewer } = useViewer();

  // An item with no `requires` is for everyone; one with it waits for the viewer
  // rather than flashing into view, which fails closed while loading.
  const items = isAuthenticated
    ? NAV_ITEMS.filter((item) => !item.requires || can(viewer, item.requires))
    : [];

  const handleAuthClick = () => {
    if (isAuthenticated) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      // Same reasoning as ProtectedRoute: somebody who followed an invitation link
      // and then clicked Login here must still accept it.
      loginWithRedirect(invitationLoginOptions());
    }
  };

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-[#0E0E0E]/95 backdrop-blur border-b border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
      style={{ height: NAVBAR_HEIGHT }}
    >
      <div className="h-full max-w-screen-2xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <img
            src={logo}
            alt="Cooktop Safety"
            className="h-10 w-auto object-contain"
          />
          {/* Separated from the logo by a rule, so the sections read as
              navigation rather than as part of the wordmark beside them. */}
          {items.length > 1 && (
            <nav
              aria-label="Sections"
              className="flex items-center gap-2 pl-6 border-l border-white/15"
            >
              {items.map((item) => {
                const active =
                  (item.alsoActiveAt ?? []).includes(pathname) ||
                  (item.matchPrefix
                    ? pathname.startsWith(item.matchPrefix)
                    : pathname === item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={
                      // BOTH states are filled. An earlier version gave only the
                      // active tab a background, which left the inactive one as
                      // bare text -- so it read as a label rather than as
                      // something to press, and nothing suggested the pair were
                      // the same kind of thing. A control that does not look
                      // pressable does not get pressed.
                      //
                      // So: a light grey pill when idle, the brand green when
                      // selected. The shape stays identical between the two, which
                      // is what makes them read as one set of tabs rather than a
                      // button next to a word.
                      active
                        ? "px-4 py-2 rounded-control text-[15px] font-semibold bg-primary/25 text-primary ring-1 ring-primary/60"
                        : "px-4 py-2 rounded-control text-[15px] font-medium bg-white/10 text-white ring-1 ring-white/15 hover:bg-white/20 hover:text-primary transition-colors"
                    }
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
        <Button
          variant={isAuthenticated ? "ghost" : "primary"}
          size="sm"
          leadingIcon={
            isAuthenticated ? (
              <LogoutIcon fontSize="small" />
            ) : (
              <LoginIcon fontSize="small" />
            )
          }
          onClick={handleAuthClick}
          aria-label={isAuthenticated ? "Log out" : "Log in"}
        >
          {isAuthenticated ? "Log out" : "Log in"}
        </Button>
      </div>
    </header>
  );
}

export default Navbar;
